/**
 * sessionLocal.ts — 이 브라우저에만 남기는 세션 값(localStorage·sessionStorage).
 *
 * 설계: docs/design/12_통합관리_계정기기설계.md §4-2 기기 ID · §7 세션 정책.
 * Firestore 와 무관한 브라우저 저장소 다루기만 모았다. 사생활 모드 등에서 저장소가 막히면 조용히 무시한다.
 *
 *  - rmc_device_id          (local)   기기 1대 = 무작위 ID 1개. 시크릿 창·사이트 데이터 삭제 시 새 기기(§10-5)
 *  - rmc_last_activity_at   (local)   마지막 활동 시각(ms). 탭 간 공유 — 유휴 4시간·60일 만료 판정
 *  - rmc_auto_login_off     (local)   이 기기에서 그 사람은 자동 로그인을 꺼 둠 — 로그인 전에 persistence 를 고르기 위한 힌트.
 *                                     값 = {uid, login_id} JSON. 같은 브라우저의 다른 사람에게는 적용하지 않는다(옛 값 "1" 은 무시)
 *  - rmc_logout_notice      (local)   앱이 스스로 로그아웃시킨 이유 — 로그인 화면이 한 번 보여 주고 지움
 *  - rmc_shared_session     (session) 이 탭 세션은 공용 PC 로그인 — 기기 문서를 만들지 않음
 *  - rmc_session_logged     (session) 이 탭 세션의 로그인·자동 로그인 기록을 이미 남김
 */
import { DEVICE_ID_STORAGE_KEY, LAST_ACTIVITY_STORAGE_KEY } from "@/domain/session";
import { isLogoutNoticeKind, type LogoutNoticeKind } from "@/domain/device_info";

const AUTO_LOGIN_OFF_KEY = "rmc_auto_login_off";
const LOGOUT_NOTICE_KEY = "rmc_logout_notice";
const SHARED_SESSION_KEY = "rmc_shared_session";
const SESSION_LOGGED_KEY = "rmc_session_logged";
const AUTO_LOGIN_LOGGED_AT_KEY = "rmc_auto_login_logged_at";
/** 안내 문구 유효 시간 — 오래된 안내가 다음 로그인 화면에 남지 않게 */
const NOTICE_TTL_MS = 10 * 60 * 1000;

function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, v: string): void {
  try { localStorage.setItem(key, v); } catch { /* 저장소 막힘 */ }
}
function lsDel(key: string): void {
  try { localStorage.removeItem(key); } catch { /* 저장소 막힘 */ }
}
function ssGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function ssSet(key: string, v: string): void {
  try { sessionStorage.setItem(key, v); } catch { /* 저장소 막힘 */ }
}
function ssDel(key: string): void {
  try { sessionStorage.removeItem(key); } catch { /* 저장소 막힘 */ }
}

function randomId(): string {
  const c: Crypto | null = typeof crypto !== "undefined" ? crypto : null;
  // randomUUID 는 보안 컨텍스트(https·localhost)에서만 있다 — 없으면 getRandomValues
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** 이 기기의 ID(없으면 만든다). Firestore 문서 ID 로 쓰므로 `__` 로 시작하지 않는다 */
export function getDeviceId(): string {
  const cur = lsGet(DEVICE_ID_STORAGE_KEY);
  if (cur && !cur.startsWith("__") && /^[A-Za-z0-9-]{8,64}$/.test(cur)) return cur;
  const id = randomId();
  lsSet(DEVICE_ID_STORAGE_KEY, id);
  return id;
}

/** 새 로그인 세션 ID(기기 문서 session_id) */
export function newSessionId(): string {
  return randomId();
}

// ── 마지막 활동 ────────────────────────────────────────────────
export function readLastActivity(): number | null {
  const n = Number(lsGet(LAST_ACTIVITY_STORAGE_KEY));
  return Number.isFinite(n) && n > 0 ? n : null;
}
export function writeLastActivity(ms: number = Date.now()): void {
  lsSet(LAST_ACTIVITY_STORAGE_KEY, String(ms));
}
export { LAST_ACTIVITY_STORAGE_KEY };

// ── 자동 로그인 꺼 둠 힌트 ─────────────────────────────────────
/**
 * 힌트가 이 사람 것인가. 로그인 전에는 uid 를 모르므로 입력한 아이디(login_id, 대소문자 무시)로 맞춘다.
 * 로그인 뒤에는 기기 문서 값으로 다시 맞춘다(device_session.service registerAndLog·AuthContext 구독).
 */
export function readAutoLoginOffHint(who: { uid?: string | null; loginId?: string | null }): boolean {
  const raw = lsGet(AUTO_LOGIN_OFF_KEY);
  if (!raw || raw === "1") return false;
  try {
    const v = JSON.parse(raw) as { uid?: unknown; login_id?: unknown };
    if (who.uid && v.uid === who.uid) return true;
    const id = (who.loginId ?? "").trim().toLowerCase();
    return !!id && typeof v.login_id === "string" && v.login_id.toLowerCase() === id;
  } catch {
    return false;
  }
}
/** off = 이 사람은 이 기기에서 자동 로그인을 꺼 둠. 켜면 그 사람 힌트만 지운다(다른 사람 힌트는 그대로) */
export function writeAutoLoginOffHint(off: boolean, who: { uid: string; loginId: string }): void {
  if (off) {
    lsSet(AUTO_LOGIN_OFF_KEY, JSON.stringify({ uid: who.uid, login_id: who.loginId }));
  } else if (readAutoLoginOffHint({ uid: who.uid })) {
    lsDel(AUTO_LOGIN_OFF_KEY);
  }
}

// ── 로그아웃 안내 ──────────────────────────────────────────────
export function setLogoutNotice(kind: LogoutNoticeKind): void {
  lsSet(LOGOUT_NOTICE_KEY, JSON.stringify({ kind, at: Date.now() }));
}
/** 안내 읽기(지우지 않음 — StrictMode 가 state 초기화 함수를 두 번 불러도 같은 값). 지우기는 clearLogoutNotice */
export function peekLogoutNotice(): LogoutNoticeKind | null {
  const raw = lsGet(LOGOUT_NOTICE_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as { kind?: unknown; at?: unknown };
    if (!isLogoutNoticeKind(v.kind)) return null;
    if (typeof v.at !== "number" || Date.now() - v.at > NOTICE_TTL_MS) return null;
    return v.kind;
  } catch {
    return null;
  }
}
export function clearLogoutNotice(): void {
  lsDel(LOGOUT_NOTICE_KEY);
}

// ── 이 탭 세션 표시 ────────────────────────────────────────────
export function markSharedSession(on: boolean): void {
  if (on) ssSet(SHARED_SESSION_KEY, "1");
  else ssDel(SHARED_SESSION_KEY);
}
export function isSharedSession(): boolean {
  return ssGet(SHARED_SESSION_KEY) === "1";
}
export function markSessionLogged(): void {
  ssSet(SESSION_LOGGED_KEY, "1");
}
export function isSessionLogged(): boolean {
  return ssGet(SESSION_LOGGED_KEY) === "1";
}
/** 로그아웃 때 이 탭 세션 표시를 지운다 */
export function clearSessionMarks(): void {
  ssDel(SHARED_SESSION_KEY);
  ssDel(SESSION_LOGGED_KEY);
}

/** 자동 로그인 기록 중복 억제(새 탭마다 남지 않게) — 마지막으로 남긴 시각 */
export function readAutoLoginLoggedAt(): number {
  const n = Number(lsGet(AUTO_LOGIN_LOGGED_AT_KEY));
  return Number.isFinite(n) ? n : 0;
}
export function writeAutoLoginLoggedAt(ms: number = Date.now()): void {
  lsSet(AUTO_LOGIN_LOGGED_AT_KEY, String(ms));
}

// ── 유휴 로그아웃 탭 간 중복 방지 ──────────────────────────────
const IDLE_LOCK_KEY = "rmc_idle_logout_at";
/** 여러 탭이 동시에 0초가 될 때 한 탭만 기록·로그아웃하게. 30초 안에 다른 탭이 잡았으면 false */
export function tryIdleLogoutLock(now: number = Date.now()): boolean {
  const prev = Number(lsGet(IDLE_LOCK_KEY));
  if (Number.isFinite(prev) && now - prev < 30_000) return false;
  lsSet(IDLE_LOCK_KEY, String(now));
  return true;
}
